import { logger } from '@/utils/logger';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import OrganizationInput from './OrganizationInput';
import EmailInput from './EmailInput';
import TwoFactorForm from './TwoFactorForm';
import { useTwoFactorAuth } from '@/hooks/useTwoFactorAuth';

interface SignInFormProps {
  organizationCode: string;
  setOrganizationCode: (code: string) => void;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  isLoading: boolean;
  show2FA: boolean;
  setShow2FA: (show: boolean) => void;
  userProfile: any;
  currentOrganization: any;
  rememberOrganization: boolean;
  handleRememberOrganization: (checked: boolean) => void;
  rememberEmail: boolean;
  handleRememberEmail: (checked: boolean) => void;
  onSignIn: () => Promise<{ requires2FA?: boolean }>;
  setTwoFactorVerified: (verified: boolean) => void;
  toast: any;
}

const SignInForm: React.FC<SignInFormProps> = ({
  organizationCode,
  setOrganizationCode,
  email,
  setEmail,
  password,
  setPassword,
  isLoading,
  show2FA,
  setShow2FA,
  userProfile,
  currentOrganization,
  rememberOrganization,
  handleRememberOrganization,
  rememberEmail,
  handleRememberEmail,
  onSignIn,
  setTwoFactorVerified,
  toast
}) => {
  const {
    twoFactorCode,
    setTwoFactorCode,
    handle2FAVerification
  } = useTwoFactorAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (show2FA) {
        await handle2FAVerification(
          userProfile,
          currentOrganization,
          setTwoFactorVerified,
          toast
        );
      } else {
        const result = await onSignIn();
        if (result.requires2FA) {
          setShow2FA(true);
        }
      }
    } catch (error: any) {
      logger.error('Sign in error:', error);
      toast({
        title: show2FA ? "2FA verification failed" : "Sign in failed",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive"
      });
    }
  };

  const handleBackTo2FA = () => {
    setShow2FA(false);
    setTwoFactorCode('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!show2FA && (
        <>
          <OrganizationInput
            organizationCode={organizationCode}
            setOrganizationCode={setOrganizationCode}
            rememberOrganization={rememberOrganization}
            onRememberChange={handleRememberOrganization}
          />

          <EmailInput
            email={email}
            setEmail={setEmail}
            rememberEmail={rememberEmail}
            onRememberChange={handleRememberEmail}
          />

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              minLength={6}
            />
          </div>
        </>
      )}

      {show2FA && (
        <TwoFactorForm
          email={email}
          twoFactorCode={twoFactorCode}
          setTwoFactorCode={setTwoFactorCode}
        />
      )}

      <div className="flex space-x-2">
        <Button
          type="submit"
          className="flex-1"
          disabled={isLoading || (show2FA && twoFactorCode.length !== 6)}
        >
          {isLoading
            ? 'Loading...'
            : show2FA
            ? 'Verify & Sign In'
            : 'Sign In'}
        </Button>

        {show2FA && (
          <Button
            type="button"
            variant="outline"
            onClick={handleBackTo2FA}
            disabled={isLoading}
          >
            Back
          </Button>
        )}
      </div>
    </form>
  );
};

export default SignInForm;
